/*
 * JMI Operator Segment Shipping & Logistics Plan
 *
 * Defines the end-to-end custody and transit plan for every jewellery
 * segment a JMI Regional Operator Hub handles. Used by the Operator page
 * to show prospective hub partners exactly how each segment moves through
 * the dual-custody network: transit mode, packaging, insurance, custody
 * level, SLA and per-lot handling economics.
 */

export const operatorSegments = [
  {
    id: 'yellow-gold',
    group: 'Gold',
    name: 'Yellow Gold',
    hallmark: 'BIS 916 (22K) • 750 (18K) • 999 (24K)',
    icon: '🟡',
    accent: '#facc15',
    accentGradient: 'linear-gradient(135deg, #fbbf24, #d97706)',
    shipMode: 'Armored Vault-to-Vault Transit',
    packaging: 'Serialized tamper-evident polyurethane pouch + HUID laser tag',
    insurance: '₹5 Crore transit insurance per consignment',
    custody: 'Dual-custody biometric handshake (driver + hub custodian)',
    sla: 'T+1 within city cluster • T+2 inter-city',
    feePerLot: '₹180 – ₹320 per lot',
    feeMin: 180,
    feeMax: 320,
    typicalKgPerMonth: '40 – 120 kg',
    corridors: 'Zaveri Bazaar (Mumbai), Karol Bagh (Delhi), Chickpet (Bangalore)',
    coverage: 'Pan-India',
    colorClass: 'gold-segment',
  },
  {
    id: 'white-gold',
    group: 'Gold',
    name: 'White Gold',
    hallmark: 'BIS 750 (18K WG) • 585 (14K WG) • Rhodium flash verified',
    icon: '⚪',
    accent: '#e2e8f0',
    accentGradient: 'linear-gradient(135deg, #e2e8f0, #94a3b8)',
    shipMode: 'Insured Bullion Courier (GPS Geofenced)',
    packaging: 'Anti-static tamper-evident pouch + serialized QR custody seal',
    insurance: '₹3 Crore transit insurance per consignment',
    custody: 'Dual-custody OTP verification at handover',
    sla: 'T+1 within city cluster',
    feePerLot: '₹150 – ₹280 per lot',
    feeMin: 150,
    feeMax: 280,
    typicalKgPerMonth: '15 – 60 kg',
    corridors: 'Surat, Jaipur, Coimbatore',
    coverage: 'South & West India corridors',
    colorClass: 'white-gold-segment',
  },
  {
    id: 'rose-gold',
    group: 'Gold',
    name: 'Rose Gold',
    hallmark: 'BIS 750 (18K RG) • 585 (14K RG)',
    icon: '🌸',
    accent: '#f9a8d4',
    accentGradient: 'linear-gradient(135deg, #f9a8d4, #ec4899)',
    shipMode: 'Insured Premium Courier',
    packaging: 'Anti-tarnish tamper-evident pouch + luxury seal',
    insurance: '₹3 Crore transit insurance per consignment',
    custody: 'Dual-custody OTP verification at handover',
    sla: 'T+1 within city cluster',
    feePerLot: '₹160 – ₹290 per lot',
    feeMin: 160,
    feeMax: 290,
    typicalKgPerMonth: '10 – 40 kg',
    corridors: 'Mumbai, Delhi, Bengaluru',
    coverage: 'Premium jewellery corridors',
    colorClass: 'rose-gold-segment',
  },
  {
    id: 'fine-silver',
    group: 'Silver',
    name: '999 Fine Silver',
    hallmark: 'BIS 999 Fine Silver hallmark stamp',
    icon: '🥈',
    accent: '#cbd5e1',
    accentGradient: 'linear-gradient(135deg, #cbd5e1, #64748b)',
    shipMode: 'Bullion Armored Carrier (BVC / Sequel)',
    packaging: 'Heavy-gauge tamper-evident pouch + barcoded tare slip',
    insurance: '₹2 Crore transit insurance per consignment',
    custody: 'Dual-custody digital manifest signature',
    sla: 'T+1 within city cluster • T+3 inter-city',
    feePerLot: '₹90 – ₹160 per lot',
    feeMin: 90,
    feeMax: 160,
    typicalKgPerMonth: '100 – 300 kg',
    corridors: 'Kolkata, Siliguri, Guwahati',
    coverage: 'East & Northeast India corridors',
    colorClass: 'silver-segment',
  },
  {
    id: 'sterling-silver',
    group: 'Silver',
    name: '925 Sterling Silver',
    hallmark: 'BIS 925 Sterling Artefact hallmark',
    icon: '🥈',
    accent: '#94a3b8',
    accentGradient: 'linear-gradient(135deg, #94a3b8, #475569)',
    shipMode: 'Insured Consolidated Freight',
    packaging: 'Tamper-evident batch pouch with net-weight certificate',
    insurance: '₹1.5 Crore transit insurance per consignment',
    custody: 'Hub custodian countersign + driver biometric',
    sla: 'T+2 within city cluster • T+4 inter-city',
    feePerLot: '₹70 – ₹140 per lot',
    feeMin: 70,
    feeMax: 140,
    typicalKgPerMonth: '80 – 250 kg',
    corridors: 'Coimbatore, Tirupur, Rajkot',
    coverage: 'South & West India corridors',
    colorClass: 'silver-segment',
  },
  {
    id: 'silver-artefacts',
    group: 'Silver',
    name: 'Silver Artefacts & Idols',
    hallmark: 'BIS 925 hallmark for artefacts',
    icon: '🏺',
    accent: '#a8a29e',
    accentGradient: 'linear-gradient(135deg, #a8a29e, #78716c)',
    shipMode: 'Specialist Artefact Carrier',
    packaging: 'Climate-controlled cushioned packaging',
    insurance: '₹1.5 Crore transit insurance per consignment',
    custody: 'Digital manifest + photography log',
    sla: 'T+2 within city cluster • T+4 inter-city',
    feePerLot: '₹80 – ₹150 per lot',
    feeMin: 80,
    feeMax: 150,
    typicalKgPerMonth: '50 – 150 kg',
    corridors: 'Kolkata, Varanasi, Madurai',
    coverage: 'Heritage artefact corridors',
    colorClass: 'silver-artefact-segment',
  },
  {
    id: 'platinum',
    group: 'Platinum & Stones',
    name: 'Pt950 Platinum',
    hallmark: 'Pt950 hallmark + purity assay certificate',
    icon: '🔗',
    accent: '#38bdf8',
    accentGradient: 'linear-gradient(135deg, #38bdf8, #0369a1)',
    shipMode: 'High-Security Dedicated Armored Escort',
    packaging: 'Reinforced serialized vault pouch + void-evident sealing',
    insurance: '₹8 Crore transit insurance per consignment',
    custody: 'Triple-verification dual-custody (escort + two hub officers)',
    sla: 'T+2 on-demand scheduled pickup',
    feePerLot: '₹400 – ₹750 per lot',
    feeMin: 400,
    feeMax: 750,
    typicalKgPerMonth: '5 – 25 kg',
    corridors: 'Mumbai, Delhi NCR, Ahmedabad',
    coverage: 'Metro hubs only (by prior appointment)',
    colorClass: 'platinum-segment',
  },
  {
    id: 'diamond-studded',
    group: 'Platinum & Stones',
    name: 'Diamond & Gemstone Studded',
    hallmark: 'IGI / GIA certified natural diamonds & coloured stones',
    icon: '💎',
    accent: '#a78bfa',
    accentGradient: 'linear-gradient(135deg, #a78bfa, #6d28d9)',
    shipMode: 'Registered Insured Courier with Chain-of-Custody Log',
    packaging: 'Gem-padded tamper-evident pouch + individual IGI/GIA packet log',
    insurance: '₹4 Crore transit insurance per consignment',
    custody: 'Assay-verified handover with stone-carat reconciliation slip',
    sla: 'T+2 within city cluster • T+5 inter-city',
    feePerLot: '₹250 – ₹500 per lot',
    feeMin: 250,
    feeMax: 500,
    typicalKgPerMonth: '2 – 15 kg (high value, low weight)',
    corridors: 'Jaipur, Surat, Kolkata',
    coverage: 'Gem & diamond cutting clusters',
    colorClass: 'diamond-segment',
  },
  {
    id: 'kundan-polki',
    group: 'Platinum & Stones',
    name: 'Kundan / Polki / Temple',
    hallmark: 'Traditional hallmarked gold base with uncut diamond / polki work',
    icon: '🪔',
    accent: '#d4af37',
    accentGradient: 'linear-gradient(135deg, #d4af37, #a16207)',
    shipMode: 'Specialist Heritage Consignment Carrier',
    packaging: 'Climate-cushioned void-evident pouch + artisan custody log',
    insurance: '₹3 Crore transit insurance per consignment',
    custody: 'Master artisan countersign at origin + hub custodian at receipt',
    sla: 'T+3 scheduled cluster route',
    feePerLot: '₹300 – ₹600 per lot',
    feeMin: 300,
    feeMax: 600,
    typicalKgPerMonth: '3 – 12 kg',
    corridors: 'Jaipur, Hyderabad, Varanasi',
    coverage: 'Heritage jewellery clusters only',
    colorClass: 'heritage-segment',
  },
  {
    id: ' temple-jewellery',
    group: 'Platinum & Stones',
    name: 'Temple & Antique Jewellery',
    hallmark: 'Traditional hallmark with antique finish certification',
    icon: '🛕',
    accent: '#ca8a04',
    accentGradient: 'linear-gradient(135deg, #ca8a04, #854d0e)',
    shipMode: 'Heritage Specialist Carrier',
    packaging: 'Museum-grade cushioned packaging + humidity control',
    insurance: '₹2.5 Crore transit insurance per consignment',
    custody: 'Master artisan + hub custodian dual verification',
    sla: 'T+3 scheduled cluster route',
    feePerLot: '₹280 – ₹550 per lot',
    feeMin: 280,
    feeMax: 550,
    typicalKgPerMonth: '2 – 10 kg',
    corridors: 'Tamil Nadu, Kerala, Andhra Pradesh',
    coverage: 'Temple jewellery corridors',
    colorClass: 'heritage-segment',
  },
  {
    id: 'fashion-jewellery',
    group: 'Fashion',
    name: 'Fashion & Artificial Jewellery',
    hallmark: 'Quality certified (no BIS for fashion)',
    icon: '💃',
    accent: '#f472b6',
    accentGradient: 'linear-gradient(135deg, #f472b6, #db2777)',
    shipMode: 'Standard Insured Courier',
    packaging: 'Protective individual packaging',
    insurance: '₹50 Lakh per consignment',
    custody: 'Standard digital manifest',
    sla: 'T+2 within city • T+4 inter-city',
    feePerLot: '₹40 – ₹80 per lot',
    feeMin: 40,
    feeMax: 80,
    typicalKgPerMonth: '50 – 200 kg',
    corridors: 'All major markets',
    coverage: 'Fashion jewellery corridors',
    colorClass: 'fashion-segment',
  },
  {
    id: 'machinery-tools',
    group: 'Equipment',
    name: 'Jewellery Machinery & Tools',
    hallmark: 'ISI marked equipment',
    icon: '⚙️',
    accent: '#6b7280',
    accentGradient: 'linear-gradient(135deg, #6b7280, #374151)',
    shipMode: 'Heavy Cargo Logistics',
    packaging: 'Industrial crate packaging',
    insurance: '₹1 Crore per consignment',
    custody: 'Digital manifest + installation verification',
    sla: 'T+3 - T+5 as per logistics',
    feePerLot: '₹500 – ₹1500 per lot',
    feeMin: 500,
    feeMax: 1500,
    typicalKgPerMonth: '500 – 2000 kg',
    corridors: 'Mumbai, Delhi, Ahmedabad',
    coverage: 'Industrial equipment corridors',
    colorClass: 'machinery-segment',
  },
];

export const operatorSegmentGroups = [
  { key: 'all', label: 'All Segments' },
  { key: 'Gold', label: 'Gold', icon: '🟡', count: operatorSegments.filter(s => s.group === 'Gold').length },
  { key: 'Silver', label: 'Silver', icon: '🥈', count: operatorSegments.filter(s => s.group === 'Silver').length },
  { key: 'Platinum & Stones', label: 'Platinum & Stones', icon: '💎', count: operatorSegments.filter(s => s.group === 'Platinum & Stones').length },
  { key: 'Fashion', label: 'Fashion', icon: '💃', count: operatorSegments.filter(s => s.group === 'Fashion').length },
  { key: 'Equipment', label: 'Equipment', icon: '⚙️', count: operatorSegments.filter(s => s.group === 'Equipment').length },
];

export const operatorShippingSummary = {
  totalSegments: operatorSegments.length,
  activeCorridors: 12,
  insuredUpto: '₹8 Crore',
  avgSLA: 'T+1 to T+5',
  minFee: '₹40 / lot',
  maxFee: '₹1500 / lot',
  totalVolumeMonthly: '300 - 3000+ kg',
  totalFeeRange: '₹40 - ₹1500 per lot',
};